import { Flex } from '@withjoy/joykit';
import React, { memo, useMemo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Car, MapMarker, Walk } from '@withjoy/joykit/icons';

// local imports
import { TravelMetric } from './TravelMetric';
import { getWalkingAndDrivingDistance } from '@shared/utils/distanceInMiles';
import { useFormatTravelDistance } from '@shared/utils/hooks/useFormatTravelDistance';

// interfaces
interface TravelMetricsOverlayProps
  extends Readonly<{
    distance: number;
  }> {}

// Max threshold for showing travel metrics
const MAX_DISTANCE_THRESHOLD = 300;

export const TravelMetricsOverlay = memo<TravelMetricsOverlayProps>(({ distance }) => {
  const formatTravelDistance = useFormatTravelDistance();

  const travelTimes = useMemo(() => getWalkingAndDrivingDistance(distance), [distance]);

  const { formattedDistance, formattedWalkingTime, formattedDrivingTime } = useMemo(
    () => ({
      formattedDistance: formatTravelDistance({
        value: distance,
        type: 'distance',
      }),
      formattedWalkingTime: formatTravelDistance({
        value: travelTimes.walking,
        type: 'time',
      }),
      formattedDrivingTime: formatTravelDistance({
        value: travelTimes.driving,
        type: 'time',
      }),
    }),
    [distance, travelTimes, formatTravelDistance],
  );

  if (distance > MAX_DISTANCE_THRESHOLD) {
    return null;
  }

  return (
    <Flex
      color="white"
      width="fit-content"
      paddingY={pxToRem(4)}
      paddingX={pxToRem(8)}
      borderRadius={pxToRem(4)}
      backgroundColor="newUIBrandPrimary"
    >
      <Flex
        gap={5}
        width="100%"
        flexWrap="nowrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <TravelMetric
          icon={Walk}
          value={formattedWalkingTime.formatted}
          unitLabel={formattedWalkingTime.unitLabel}
        />
        <TravelMetric
          icon={Car}
          value={formattedDrivingTime.formatted}
          unitLabel={formattedDrivingTime.unitLabel}
        />
        <TravelMetric
          icon={MapMarker}
          value={formattedDistance.formatted}
          unitLabel={formattedDistance.unitLabel}
        />
      </Flex>
    </Flex>
  );
});

TravelMetricsOverlay.displayName = 'TravelMetricsOverlay';
